import {
  type Branch,
  type Feature,
  hasFeature,
  type Permission,
  type TenantSettings,
} from "@zeeta/shared";
import { createContext, type ReactNode, useCallback, useContext, useMemo, useState } from "react";
import type { TenantOverview } from "./queries";

interface TenantContextValue {
  tenantId: string;
  settings: TenantSettings;
  branches: Branch[];
  branch: Branch;
  setBranchId: (id: string) => void;
  role: string;
  can: (permission: Permission) => boolean;
  plan: TenantOverview["plan"];
  /** Whether the current plan includes a feature (FR-BIL-01). */
  allows: (feature: Feature) => boolean;
  support: TenantOverview["support"];
}

const TenantContext = createContext<TenantContextValue | null>(null);

function storageKey(tenantId: string) {
  return `zeeta.branch.${tenantId}`;
}

export function TenantProvider({
  overview,
  children,
}: {
  overview: TenantOverview;
  children: ReactNode;
}) {
  const visible = useMemo(() => {
    const scope = overview.membership.branchScope;
    return overview.branches.filter((b) => b.active && (!scope || scope.includes(b.id)));
  }, [overview]);

  const [branchId, setBranchState] = useState<string>(() => {
    try {
      const saved = localStorage.getItem(storageKey(overview.tenantId));
      if (saved && visible.some((b) => b.id === saved)) return saved;
    } catch {
      // Storage may be unavailable; fall back to the main branch.
    }
    return (visible.find((b) => b.isMain) ?? visible[0])?.id ?? "";
  });

  const setBranchId = useCallback(
    (id: string) => {
      setBranchState(id);
      try {
        localStorage.setItem(storageKey(overview.tenantId), id);
      } catch {
        // Ignore storage failures.
      }
    },
    [overview.tenantId],
  );

  const value = useMemo<TenantContextValue>(() => {
    const permissions = new Set(overview.membership.permissions);
    const branch = visible.find((b) => b.id === branchId) ?? visible[0];
    if (!branch) throw new Error("No branch available");
    return {
      tenantId: overview.tenantId,
      settings: overview.settings,
      branches: visible,
      branch,
      setBranchId,
      role: overview.membership.role,
      can: (p) => permissions.has(p),
      plan: overview.plan,
      // Older cached overviews have no entitlements yet; fall back to the plan's defaults.
      allows: (f) =>
        overview.plan.entitlements
          ? overview.plan.entitlements.features.includes(f)
          : hasFeature(overview.plan.id, f),
      support: overview.support ?? null,
    };
  }, [overview, visible, branchId, setBranchId]);

  return <TenantContext.Provider value={value}>{children}</TenantContext.Provider>;
}

export function useTenant(): TenantContextValue {
  const ctx = useContext(TenantContext);
  if (!ctx) throw new Error("useTenant must be used inside TenantProvider");
  return ctx;
}

/** Display name in the active language, falling back to the other. */
export function useLocalName() {
  return (locale: "bn" | "en", row: { nameEn: string; nameBn: string }) =>
    locale === "bn" ? row.nameBn || row.nameEn : row.nameEn || row.nameBn;
}
